import promoSubscriptionTags from '@/shared/assets/videos/promo_sub.png'
import { VideoCard, VideoConferenceCard } from '@/entities/video'
import type { VideoCardProps, VideoConferenceCardProps } from '@/entities/video'
import { cn, useDraggableHorizontalScroll } from '@/shared/lib'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'

const promoTokens = designTokens.components.videoSubscriptionPromo

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.lg, {
    fontSize: '18px',
    lineHeight: 'normal',
    fontWeight: 600,
  }),
}

const bodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.md, {
    lineHeight: 'normal',
  }),
}

const ctaStyle = {
  width: promoTokens.ctaWidth,
  height: promoTokens.ctaHeight,
  borderRadius: promoTokens.ctaRadius,
  border: 'none',
  backgroundColor: promoTokens.ctaBackground,
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.xsSemibold),
  cursor: 'pointer',
}

const highlightStyle = {
  color: promoTokens.headingAccent,
}

const shelfTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.onboarding.titleDesktop),
}

type VideoPromoVideoItem = {
  id: string
  kind?: 'video'
} & VideoCardProps

type VideoPromoConferenceItem = {
  id: string
  kind: 'conference'
} & VideoConferenceCardProps

export type VideoPromoItem = VideoPromoVideoItem | VideoPromoConferenceItem

type VideoSubscriptionPromoProps = {
  marketingItems: VideoPromoItem[]
  aiItems: VideoPromoItem[]
  className?: string
  onCtaClick?: () => void
}

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="9" width="16" height="12" rx="4" stroke="#FEFEFE" strokeWidth="1.5" />
    <path
      d="M12 16L12 14"
      stroke="#FEFEFE"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 9V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7L8 9"
      stroke="#FEFEFE"
      strokeWidth="1.5"
    />
  </svg>
)

LockIcon.displayName = 'LockIcon'

const toLockedItems = (items: VideoPromoItem[]): VideoPromoItem[] =>
  items.map((item) => ({
    ...item,
    className: cn('select-none opacity-50', item.className),
  }))

const marketingTitle = (
  <span className="inline-flex items-center gap-2">
    <LockIcon />
    <span>
      ТОП-10 материалов по <span style={highlightStyle}>маркетингу</span>
    </span>
  </span>
)

const aiTitle = (
  <span className="inline-flex items-center gap-2">
    <LockIcon />
    <span>AI, ML-технологии и нейросети</span>
  </span>
)

const renderItemsWithDragAwareClick = (
  items: VideoPromoItem[],
  bindDragAwareClick: (handler: (() => void) | undefined) => (() => void) | undefined
) =>
  items.map((item) => {
    if (item.kind === 'conference') {
      const { id, kind: _kind, ...card } = item
      return <VideoConferenceCard key={id} {...card} onClick={bindDragAwareClick(card.onClick)} />
    }

    const { id, kind: _kind, ...card } = item
    return <VideoCard key={id} {...card} onClick={bindDragAwareClick(card.onClick)} />
  })

export function VideoSubscriptionPromo({
  marketingItems,
  aiItems,
  className,
  onCtaClick,
}: VideoSubscriptionPromoProps) {
  const [marketingScrollRef, isMarketingDragging, bindMarketingDragAwareClick] =
    useDraggableHorizontalScroll<HTMLDivElement>()
  const [aiScrollRef, isAiDragging, bindAiDragAwareClick] =
    useDraggableHorizontalScroll<HTMLDivElement>()

  return (
    <section
      className={cn('mx-auto flex w-full flex-col', className)}
      style={{
        maxWidth: promoTokens.width,
        padding: promoTokens.padding,
        gap: promoTokens.gap,
        borderRadius: promoTokens.radius,
        background: promoTokens.background,
        boxShadow: promoTokens.shadow,
      }}
    >
      <div className="flex w-full flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
        <div
          className="flex w-full flex-col gap-3"
          style={{
            maxWidth: promoTokens.headlineWidth,
            transform: `translate(${promoTokens.headlineOffsetX}, ${promoTokens.headlineOffsetY})`,
          }}
        >
          <h3 style={titleStyle}>Больше материалов доступно в подписке</h3>
          <p style={bodyStyle}>
            Чтобы начать изучение материала и получить доступ ко множеству других, вам нужно
            оформить доступ к epic growth community.
          </p>
          <button type="button" style={ctaStyle} onClick={onCtaClick}>
            Оформить подписку
          </button>
        </div>
        <img
          src={promoSubscriptionTags}
          alt=""
          aria-hidden="true"
          className="w-full max-w-full xl:w-auto"
          style={{
            maxWidth: promoTokens.chipsWidth,
            transform: `translate(${promoTokens.chipsOffsetX}, ${promoTokens.chipsOffsetY})`,
          }}
        />
      </div>
      <div className="flex w-full flex-col gap-8">
        <section className="flex w-full flex-col gap-4">
          <h3 style={shelfTitleStyle}>{marketingTitle}</h3>
          <div
            ref={marketingScrollRef}
            className={cn(
              'scrollbar-none flex w-full gap-4 overflow-x-auto pb-1',
              isMarketingDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
            )}
          >
            {renderItemsWithDragAwareClick(
              toLockedItems(marketingItems),
              bindMarketingDragAwareClick
            )}
          </div>
        </section>
        <section className="flex w-full flex-col gap-4">
          <h3 style={shelfTitleStyle}>{aiTitle}</h3>
          <div
            ref={aiScrollRef}
            className={cn(
              'scrollbar-none flex w-full gap-4 overflow-x-auto pb-1',
              isAiDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
            )}
          >
            {renderItemsWithDragAwareClick(toLockedItems(aiItems), bindAiDragAwareClick)}
          </div>
        </section>
      </div>
    </section>
  )
}

VideoSubscriptionPromo.displayName = 'VideoSubscriptionPromo'
